import { useEffect, useMemo, useState, type ReactNode } from "react";
import { supportedTcgs } from "@dropdex/config";
import type { NotificationPreferences } from "@dropdex/domain";
import type {
  DropDexAppClient,
  DropDexOnboardingAlertType,
  DropDexOnboardingProfile
} from "./index";

const defaultTcgs = ["pokemon", "one-piece"];
const defaultAlerts: DropDexOnboardingAlertType[] = [
  "online-restocks",
  "in-store-restocks",
  "smart-watchlist",
  "releases"
];
const alertChoices: Array<{ id: DropDexOnboardingAlertType; icon: string; title: string; copy: string }> = [
  { id: "online-restocks", icon: "🚨", title: "Online Restocks", copy: "Products becoming available online." },
  { id: "in-store-restocks", icon: "🏪", title: "In-Store Restocks", copy: "Nearby inventory at physical stores." },
  { id: "smart-watchlist", icon: "🔔", title: "Smart Watchlist", copy: "Products you specifically follow." },
  { id: "releases", icon: "📅", title: "Releases", copy: "Upcoming launch reminders." },
  { id: "trending", icon: "🔥", title: "Trending", copy: "Major DropDex demand changes." },
  { id: "news", icon: "📰", title: "TCG News", copy: "Important news for your selected games." }
];

export interface DropDexOnboardingGateProps {
  client: DropDexAppClient;
  logoSrc: string;
  platform: "mobile" | "desktop";
  children: (profile: DropDexOnboardingProfile, justCompleted: boolean) => ReactNode;
}

export function DropDexOnboardingGate({ client, logoSrc, platform, children }: DropDexOnboardingGateProps) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [profile, setProfile] = useState<DropDexOnboardingProfile | null>(null);
  const [justCompleted, setJustCompleted] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setStatus("loading");
    void client.getOnboarding().then((value) => {
      if (!active) return;
      setProfile(value);
      setStatus("ready");
    }).catch(() => {
      if (active) setStatus("error");
    });
    return () => { active = false; };
  }, [attempt, client]);

  if (status === "loading") return <OnboardingState title="Preparing your DropDex…" copy="Loading your saved interests and alert preferences." />;
  if (status === "error" || !profile) return <OnboardingState title="Onboarding unavailable" copy="We could not load your account setup."><button className="button button-gold" onClick={() => setAttempt((value) => value + 1)}>Try again</button></OnboardingState>;
  if (profile.completed) return <>{children(profile, justCompleted)}</>;
  return <DropDexOnboarding client={client} initial={profile} logoSrc={logoSrc} platform={platform} onComplete={(value) => { setProfile(value); setJustCompleted(true); }} />;
}

function OnboardingState({ title, copy, children }: { title: string; copy: string; children?: ReactNode }) {
  return <main className="onboarding-state"><section><span className="onboarding-bolt">⚡</span><h1>{title}</h1><p>{copy}</p>{children}</section></main>;
}

function DropDexOnboarding({
  client,
  initial,
  logoSrc,
  platform,
  onComplete
}: {
  client: DropDexAppClient;
  initial: DropDexOnboardingProfile;
  logoSrc: string;
  platform: "mobile" | "desktop";
  onComplete: (profile: DropDexOnboardingProfile) => void;
}) {
  const [step, setStep] = useState(1);
  const [selectedTcgs, setSelectedTcgs] = useState<string[]>(initial.selectedTcgs.length ? initial.selectedTcgs : defaultTcgs);
  const [alertTypes, setAlertTypes] = useState<DropDexOnboardingAlertType[]>(initial.alertTypes.length ? initial.alertTypes : defaultAlerts);
  const [zipCode, setZipCode] = useState(initial.zipCode || "");
  const [radiusMiles, setRadiusMiles] = useState(initial.radiusMiles || 50);
  const [nearbyNotifications, setNearbyNotifications] = useState(initial.nearbyNotificationsEnabled);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const summary = useMemo(() => alertChoices.filter((choice) => alertTypes.includes(choice.id)), [alertTypes]);

  function toggleTcg(id: string) {
    setSelectedTcgs((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
    setMessage("");
  }
  function toggleAlert(id: DropDexOnboardingAlertType) {
    setAlertTypes((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  }
  function continueFromTcgs() {
    if (!selectedTcgs.length) {
      setMessage("Choose at least one TCG to personalize your feed.");
      return;
    }
    setStep(3);
  }
  function continueFromLocation() {
    if (zipCode && !/^[0-9]{5}$/.test(zipCode)) {
      setMessage("Enter a valid five-digit ZIP code or leave it blank.");
      return;
    }
    setMessage("");
    setStep(5);
  }

  async function requestDeviceNotifications() {
    if (!("Notification" in globalThis)) return false;
    if (globalThis.Notification.permission === "granted") return true;
    if (globalThis.Notification.permission === "denied") return false;
    return (await globalThis.Notification.requestPermission()) === "granted";
  }

  async function finish(requestNotifications: boolean) {
    setSaving(true);
    setMessage("");
    try {
      let permissionGranted = false;
      if (requestNotifications) permissionGranted = await requestDeviceNotifications();
      if (zipCode) {
        await client.updateInStoreRestockPreference({
          zipCode,
          radiusMiles,
          notificationsEnabled: nearbyNotifications
        });
      }
      const currentPreferences = await client.getNotificationPreferences();
      const tcgs = Object.fromEntries(supportedTcgs.map((tcg) => [tcg.id, selectedTcgs.includes(tcg.id)])) as NotificationPreferences["tcgs"];
      await client.updateNotificationPreferences({
        ...currentPreferences,
        globalPush: currentPreferences.globalPush || (platform === "mobile" && permissionGranted),
        globalDesktop: currentPreferences.globalDesktop || (platform === "desktop" && permissionGranted),
        news: alertTypes.includes("news"),
        tcgs,
        statuses: {
          ...currentPreferences.statuses,
          "in-stock": alertTypes.includes("online-restocks"),
          restocked: alertTypes.includes("online-restocks"),
          preorder: alertTypes.includes("releases")
        }
      });
      const saved = await client.updateOnboarding({ selectedTcgs, alertTypes, completed: true });
      onComplete({
        ...saved,
        zipCode: zipCode || null,
        radiusMiles,
        nearbyNotificationsEnabled: Boolean(zipCode) && nearbyNotifications
      });
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Unable to save onboarding.");
      setSaving(false);
    }
  }

  const canGoBack = step > 1;
  return <main className="onboarding-shell"><section className="onboarding-card"><header className="onboarding-header"><div className="onboarding-brand"><img src={logoSrc} alt="" /><strong>DropDex Alerts</strong></div><span>{step} of 5</span></header><div className="onboarding-progress" aria-label={`Onboarding step ${step} of 5`}><i style={{ width: `${step * 20}%` }} /></div><div className="onboarding-content">
    {step === 1 && <div className="onboarding-welcome"><span className="onboarding-bolt">⚡</span><small>WELCOME TO DROPDEX</small><h1>Never miss the drop.</h1><p>Track TCG restocks, discover what is trending, monitor products, and get alerts built around what you collect.</p><button className="button button-gold onboarding-primary" onClick={() => setStep(2)}>Get Started</button><button className="onboarding-link" onClick={() => void finish(false)} disabled={saving}>Already set up? Skip →</button></div>}
    {step === 2 && <><div className="onboarding-copy"><small>PERSONALIZE YOUR FEED</small><h1>What do you collect?</h1><p>Choose the TCGs you want DropDex to personalize for you. You can change these later.</p></div><div className="onboarding-choice-grid tcg-choice-grid">{supportedTcgs.map((tcg) => <button className={selectedTcgs.includes(tcg.id) ? "selected" : ""} onClick={() => toggleTcg(tcg.id)} key={tcg.id}><span>{selectedTcgs.includes(tcg.id) ? "✓" : "+"}</span><strong>{tcg.name}</strong></button>)}</div></>}
    {step === 3 && <><div className="onboarding-copy"><small>ALERT INTERESTS</small><h1>What should DropDex watch?</h1><p>Choose the signals that matter to you. Device permissions come at the end.</p></div><div className="onboarding-alert-list">{alertChoices.map((choice) => <button className={alertTypes.includes(choice.id) ? "selected" : ""} onClick={() => toggleAlert(choice.id)} key={choice.id}><span>{choice.icon}</span><div><strong>{choice.title}</strong><small>{choice.copy}</small></div><i aria-hidden="true" /></button>)}</div></>}
    {step === 4 && <><div className="onboarding-copy"><small>OPTIONAL · LOCAL ALERTS</small><h1>Find restocks near you.</h1><p>Use a ZIP code instead of precise location. Your location can be changed anytime.</p></div><label className="onboarding-field"><span>ZIP code</span><input inputMode="numeric" maxLength={5} value={zipCode} onChange={(event) => { setZipCode(event.target.value.replace(/\D/g, "").slice(0, 5)); setMessage(""); }} placeholder="10001" /></label><fieldset className="onboarding-radius"><legend>Search radius</legend>{[10, 25, 50, 100].map((radius) => <button type="button" className={radiusMiles === radius ? "selected" : ""} onClick={() => setRadiusMiles(radius)} key={radius}>{radius} mi</button>)}</fieldset><label className="onboarding-check"><input type="checkbox" checked={nearbyNotifications} onChange={(event) => setNearbyNotifications(event.target.checked)} /><span>Notify me about nearby restocks</span></label></>}
    {step === 5 && <><div className="onboarding-copy onboarding-notification-copy"><span className="onboarding-bolt">🔔</span><small>ONE LAST STEP</small><h1>Don't miss the next drop.</h1><p>DropDex can alert you when products you care about restock, release, or start trending.</p></div><div className="onboarding-summary"><strong>Your alerts</strong><div>{selectedTcgs.map((id) => <span key={id}>✓ {supportedTcgs.find((tcg) => tcg.id === id)?.shortName}</span>)}{summary.map((choice) => <span key={choice.id}>✓ {choice.title}</span>)}{zipCode && <span>✓ Nearby restocks · {radiusMiles} mi</span>}</div></div><button className="button button-gold onboarding-primary" disabled={saving} onClick={() => void finish(true)}>{saving ? "Saving…" : "🔔 Enable Notifications"}</button><button className="onboarding-link" disabled={saving} onClick={() => void finish(false)}>Maybe Later</button></>}
    {message && <p className="onboarding-error" role="alert">{message}</p>}
  </div>{step > 1 && step < 5 && <footer className="onboarding-actions">{canGoBack && <button className="button button-ghost" onClick={() => { setStep((value) => value - 1); setMessage(""); }}>Back</button>}<button className="button button-gold" onClick={() => step === 2 ? continueFromTcgs() : step === 3 ? setStep(4) : continueFromLocation()}>Continue</button></footer>}</section></main>;
}
